
import {Dao} from '@chaysehartsuff/active-html';
import UserImage from '../../models/UserImage.js';
import UserImageFolders from '../../models/UserImageFolders.js';
import {Element} from '@chaysehartsuff/active-html';
import {SpinnerIcon} from '@chaysehartsuff/active-html';
import {FolderIcon} from '@chaysehartsuff/active-html';
import {PencilIcon} from '@chaysehartsuff/active-html';
import {ProgressBar} from '@chaysehartsuff/active-html';
import {TrashIcon} from '@chaysehartsuff/active-html';
import {ContentOverlay}  from '@chaysehartsuff/active-html';
import {Color} from '@chaysehartsuff/active-html';
import {Button} from '@chaysehartsuff/active-html';

/**
 * Browse, Upload, Delete, Organize the User's photos
 */
export default class PhotoManager extends Element {

    /**
     * @type {UserImage[]} user_images
     */
    user_images;

    /**
     * @type {Element} photoGrid;
     */
    photoGrid;

    /**
     * @type {Element} backButton;
     */
    backButton;

    /**
     * Displays the current folder path
     * @type {Element} path
     */
    path;

    /**
     * @type {ProgressBar}
     */
    uploadProgressBar;

    /**
     * @type {Element}
     */
    useButton;

    /**
     * @type {Number} currentFolderId
     */
    currentFolderId;

    /**
     * @type {boolean} canSelect
     */
    canSelect;

    /**
     * @type {boolean} multiSelect
     */
    multiSelect;

    /**
     * @type {String[]} selectedImages
     */
    selectedImages;

    // EVENTS
    static get onUserSelectionEvent() { return 'onUserSelection'; }
    static get onCloseEvent() { return 'onClose'; }
    static get onCancelEvent() { return 'onCancel'; }

    /**
     */
    constructor(canSelect = true, multiSelect = true, zIndex = 50){
        super('div');

        this.canSelect = canSelect;
        this.multiSelect = multiSelect;
        this.selectedImages = [];

        this.z_index = zIndex;

        this.addClass('photo-viewer-container text-black');
        this.addAttribute('data-photo-viewer-id', this.getId());

        // --- Build Modal Backdrop ---
        this.modalBackdrop = new Element('div')
            .addClass('fixed')
            .addClass('inset-0')
            .addClass('bg-gray-200/50')
            .addClass('z-' + zIndex)
            .addClass('photo-viewer-backdrop');

        // --- Build Modal ---
        const modalContainer = new Element('div')
            .addClass('flex')
            .addClass('items-center')
            .addClass('justify-center')
            .addClass('min-h-screen')
            .addClass('p-4');

        const modal = new Element('div')
            .addClass('bg-white')
            .addClass('rounded-lg')
            .addClass('shadow-xl')
            .addClass('w-4/5')
            .addClass('max-h-200')
            .addClass('overflow-hidden')
            .addClass('photo-viewer-modal');

        // --- Modal Header ---
        const modalHeader = this.createModalHeader();

        // --- Modal Body ---
        const modalBody = this.createModalBody();

        // --- Modal Footer ---
        const modalFooter = this.createModalFooter();

        // Assemble Modal
        modal.addContent(modalHeader);
        modal.addContent(modalBody);
        modal.addContent(modalFooter);
        modalContainer.addContent(modal);
        this.modalBackdrop.addContent(modalContainer);

        // Add backdrop to the main container
        this.addContent(this.modalBackdrop);

        // Event to close modal when clicking on the backdrop itself
        this.modalBackdrop.onEvent('click', (e) => {
            if (e.target === this.modalBackdrop.bindedElement) {
                this.close();
                this.event(PhotoManager.onCancelEvent, this);
            }
        });
        this.refreshUserImages();
    }

    /**
     * Creates the modal header element.
     * @returns {Element}
     */
    createModalHeader() {
        const header = new Element('div')
            .addClass('flex')
            .addClass('items-center')
            .addClass('justify-between')
            .addClass('p-4')
            .addClass('border-b')
            .addClass('border-gray-200');

        const leftSection = new Element('div').addClass('flex').addClass('items-center').addClass('space-x-2');
        this.backButton = new Element('button')
            .addAttribute('type', 'button')
            .addClass('back-button').addClass('text-gray-400').addClass('hover:text-gray-600')
            .addContent('<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path></svg>');
        
        const titleSection = new Element('div');
        const title = new Element('h3').addClass('text-lg').addClass('font-semibold').addClass('text-gray-900').addContent('Photo Viewer');
        this.path = new Element('span').addClass('text-sm').addClass('text-gray-500').addContent('/');
        titleSection.addContent(title).addContent(this.path);

        leftSection.addContent(this.backButton).addContent(titleSection);

        const closeButton = new Element('button')
            .addAttribute('type', 'button')
            .addClass('text-gray-400').addClass('hover:text-gray-600').addClass('photo-viewer-close')
            .addContent('<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>')
            .onEvent('click', () => {
                this.close();
                this.event(PhotoManager.onCancelEvent, this);
            });

        header.addContent(leftSection).addContent(closeButton);
        return header;
    }

    /**
     * Creates the modal body element.
     * @returns {Element}
     */
    createModalBody() {
        const body = new Element('div')
            .addClass('p-4').addClass('min-h-150 max-h-150').addClass('overflow-y-auto');

        // Create and add the progress bar
        this.uploadProgressBar = new ProgressBar();
        this.uploadProgressBar.addClass('mb-4'); // Add some space below the bar
        body.addContent(this.uploadProgressBar);

        this.photoGrid = new Element('div')
            .addClass('photo-grid').addClass('p-4').addClass('grid').addClass('grid-cols-2')
            .addClass('md:grid-cols-3').addClass('lg:grid-cols-4').addClass('gap-4')
            .addClass('overflow-y-auto m-h-250');

        body.addContent(this.photoGrid);
        return body;
    }

    /**
     * Creates the modal footer element.
     * @returns {Element}
     */
    createModalFooter() {
        let deleteBtnZIndex = this.z_index + 10;
        const footer = new Element('div')
            .addClass('flex').addClass('items-center').addClass('justify-between')
            .addClass('p-4').addClass('border-t').addClass('border-gray-200').addClass('bg-gray-50');

        const selectionInfo = new Element('div');
        const selectionText = new Element('span').addClass('text-sm mx-5').addClass('text-gray-600')
            .addContent('<span id="selected-count">0</span> selected');
        selectionInfo.addContent(selectionText);

        const buttonGroup = new Element('div').addClass('flex').addClass('items-center').addClass('space-x-2');

        const newFolderButton = new Element('button')
            .addAttribute('type', 'button')
            .addClass('px-4').addClass('py-2 mx-5').addClass('text-sm').addClass('font-medium').addClass('text-white')
            .addClass('bg-gray-500').addClass('hover:bg-gray-600').addClass('border').addClass('border-transparent')
            .addClass('rounded-md').addClass('shadow-sm')
            .addContent('New folder +');
        newFolderButton.onEvent('click', () => {
            let folderInput =                         new Element('input')
                            .addAttribute('type', 'text').addAttribute('value', 'Untitled')
                            .addClass('mx-auto w-full')
                            .addClass('p-2')
                            .addClass('border')
                            .addClass('border-gray-300')
                            .addClass('rounded-md')
                            .addClass('shadow-sm');
            let folderName = new ContentOverlay(
                new Element()
                    .addClass('bg-white p-10 w-1/3')
                    .addContent(
                        new Element()
                            .addContent("New Name")
                            .addClass('my-5'))
                    .addContent(
                        folderInput
                    )
                    .addContent(
                        new Element().addClass('flex my-5 items-center')
                            .addContent(
                                new Button('Create', Color.BLUE)
                                    .onEvent('click', () => {
                                        Dao.request('/folder/create', {
                                            display_name: folderInput.getValue(),
                                            folder_id: this.folderId
                                        }, (response) => {
                                            this.refreshUserImages();
                                            this.updateSelectionUI();
                                        })
                                        this.removeContent(folderName);
                                    }).addClass('mr-2')
                            )
                            .addContent(
                                new Button('Cancel', Color.GRAY)
                                    .onEvent('click', () => {
                                        this.removeContent(folderName);
                                    }).addClass('mx-2')
                            )
                    ), deleteBtnZIndex
            ).addListener(ContentOverlay.onCloseEvent, () => { this.removeContent(folderName); });
            this.addContent(folderName);
        });
        selectionInfo.addContent(newFolderButton);
        
        this.deleteSelectionButton = new Element('button')
            .addAttribute('type', 'button')
            .addClass('px-4').addClass('py-2').addClass('text-sm').addClass('font-medium').addClass('text-white')
            .addClass('bg-red-600').addClass('hover:bg-red-700').addClass('border').addClass('border-transparent')
            .addClass('rounded-md').addClass('shadow-sm').addClass('hidden') // Hidden by default
            .addContent('Delete');

        this.deleteSelectionButton.onEvent('click', () => {
            if (this.selectedImages.length > 0) {
                this.deleteImages(this.selectedImages);
            }
        });
        selectionInfo.addContent(this.deleteSelectionButton);

        const cancelButton = new Element('button')
            .addAttribute('type', 'button')
            .addClass('px-4').addClass('py-2').addClass('text-sm').addClass('font-medium').addClass('text-gray-700')
            .addClass('bg-white').addClass('border').addClass('border-gray-300').addClass('rounded-md')
            .addClass('shadow-sm').addClass('hover:bg-gray-50')
            .onEvent('click', () => {
                this.close();
                this.event(PhotoManager.onCancelEvent, this);
            });
        cancelButton.addContent('Cancel');

        buttonGroup.addContent(cancelButton);

        if (this.canSelect) {
            this.useButton = new Element('button')
                .addAttribute('type', 'button')
                .addClass('px-4').addClass('py-2').addClass('text-sm').addClass('font-medium').addClass('text-white')
                .addClass('bg-gray-400').addClass('cursor-not-allowed') // Set initial disabled style
                .addClass('border').addClass('border-transparent').addClass('rounded-md')
                .addClass('shadow-sm').addClass('focus:outline-none').addClass('focus:ring-2')
                .addClass('focus:ring-offset-2').addClass('focus:ring-blue-500').addClass('select-button')
                .addContent('Use').addAttribute('disabled', true);

            this.useButton.onEvent('click', () => {
                if (this.selectedImages.length > 0) {
                    if(this.multiSelect){
                        this.event(PhotoManager.onUserSelectionEvent, this.selectedImages);
                    } else {
                        this.event(PhotoManager.onUserSelectionEvent, this.selectedImages[0]);
                    }
                    this.close();
                }
            });
            buttonGroup.addContent(this.useButton);
        }

        footer.addContent(selectionInfo).addContent(buttonGroup);
        return footer;
    }

    refreshUserImages(folderId = null){
        this.selectedImages = [];
        this.currentFolderId = folderId;
        const loadingSpinnerContainer = new Element('div')
            .addClass('col-span-full').addClass('flex').addClass('justify-center')
            .addClass('items-center').addClass('py-12').addClass('loading-spinner')
            .addContent(new SpinnerIcon());

        this.photoGrid.clearContent().addContent(loadingSpinnerContainer);
        let url = folderId == null ? "/folder-contents" : "/folder-contents/" + folderId;

        Dao.request(url, {}, (response) => {
            if(folderId == null){
                this.path.setContent("/"); // null defaults to 'root' directory
            } else {
                this.path.setContent(response.current_folder.full_path);
            }
            this.photoGrid.clearContent();

            this.backButton.onEvent('click', (e) => {this.refreshUserImages(response.parent_folder_id); this.updateSelectionUI()})

            // --- Upload Container ---
            const uploadContainer = new Element('div')
                .addClass('cursor-pointer').addClass('group').addClass('flex').addClass('flex-col')
                .addClass('items-center').addClass('justify-center').addClass('p-2')
                .addClass('border-2').addClass('border-dashed').addClass('border-gray-300')
                .addClass('rounded-lg').addClass('hover:bg-gray-50').addClass('hover:border-blue-500')
                .addClass('transition-all').addClass('upload-container');

            const uploadIcon = new Element('div').addClass('w-16').addClass('h-16').addClass('text-gray-400');
            uploadIcon.addContent('<svg class="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>');

            const uploadText = new Element('span')
                .addClass('text-sm').addClass('font-medium').addClass('text-gray-700').addClass('mt-2')
                .addContent('Upload Photos');

            uploadContainer.addContent(uploadIcon).addContent(uploadText);

            // Drag and drop events
            uploadContainer.onEvent('dragover', (e) => {
                e.preventDefault();
                e.stopPropagation();
                uploadContainer.addClass('bg-blue-50').addClass('border-blue-600');
            });

            uploadContainer.onEvent('dragleave', (e) => {
                e.preventDefault();
                e.stopPropagation();
                uploadContainer.removeClass('bg-blue-50').removeClass('border-blue-600');
            });

            uploadContainer.onEvent('drop', (e) => {
                e.preventDefault();
                e.stopPropagation();
                uploadContainer.removeClass('bg-blue-50').removeClass('border-blue-600');
                this.handleFiles(e.dataTransfer.files);
            });

            // Click event to open file browser
            uploadContainer.onEvent('click', () => {
                // Create a hidden file input element
                const fileInput = document.createElement('input');
                fileInput.type = 'file';
                fileInput.multiple = true;
                // Use the same allowed types as handleFiles
                fileInput.accept = 'image/jpeg, image/png, image/gif, image/webp, image/bmp, image/tiff, image/svg+xml';
                fileInput.style.display = 'none';

                // When files are selected, pass them to the handler
                fileInput.onchange = (e) => {
                    if (e.target.files.length) {
                        this.handleFiles(e.target.files);
                    }
                    // Clean up by removing the input from the DOM
                    document.body.removeChild(fileInput);
                };

                // Append to the body and trigger the click
                document.body.appendChild(fileInput);
                fileInput.click();
            });

            this.photoGrid.addContent(uploadContainer);

            // Render Folders
            response.data.folders.forEach(folderData => {
                const folderContainer = new Element('div')
                    .addClass('cursor-pointer').addClass('group').addClass('relative') // Added relative for positioning
                    .addClass('flex').addClass('flex-col').addClass('items-center').addClass('justify-center')
                    .addClass('p-2').addClass('border').addClass('border-gray-200').addClass('rounded-lg')
                    .addClass('hover:bg-gray-100').addClass('hover:shadow-md').addClass('transition-all');

                const folderIcon = new Element('div').addClass('w-16').addClass('h-16').addClass('text-blue-500');
                folderIcon.addContent(new FolderIcon());

                const folderNameContainer = new Element('div').addClass('relative').addClass('mt-2');

                const folderNameSpan = new Element('span')
                    .addClass('text-sm').addClass('font-medium').addClass('text-gray-700')
                    .addClass('truncate').addContent(folderData.display_name);

                const editInput = new Element('input')
                    .addAttribute('type', 'text').addAttribute('value', folderData.display_name)
                    .addClass('text-sm').addClass('p-1').addClass('border').addClass('rounded-md')
                    .addClass('shadow-sm').addClass('hidden'); // Hidden by default

                const okButton = new Element('button')
                    .addClass('absolute').addClass('right-[-35px]').addClass('top-0').addClass('px-2').addClass('py-1')
                    .addClass('text-sm').addClass('font-medium').addClass('text-white').addClass('bg-green-600')
                    .addClass('hover:bg-green-700').addClass('border').addClass('border-transparent')
                    .addClass('rounded-md').addClass('shadow-sm').addClass('hidden') // Hidden by default
                    .addContent('OK');

                folderNameContainer.addContent(folderNameSpan).addContent(editInput).addContent(okButton);

                // --- Edit Button for Folder ---
                const editButton = new Element('button')
                    .addClass('absolute').addClass('top-1').addClass('right-10').addClass('p-1') // Positioned next to delete
                    .addClass('bg-white').addClass('rounded-full').addClass('shadow')
                    .addClass('hover:bg-blue-100 h-8 w-8').addClass('hidden'); // Hidden by default
                
                const pencilIcon = new PencilIcon();
                pencilIcon.addClass('w-full');
                editButton.addContent(pencilIcon);

                // --- Delete Button for Folder ---
                const deleteButton = new Element('button')
                    .addClass('absolute').addClass('top-1').addClass('right-1').addClass('p-1')
                    .addClass('bg-white').addClass('rounded-full').addClass('shadow')
                    .addClass('hover:bg-red-100 h-8 w-8').addClass('hidden'); // Hidden by default
                
                const trashIcon = new TrashIcon();
                trashIcon.addClass('w-full');
                deleteButton.addContent(trashIcon);

                // --- Event Handlers ---
                editButton.onEvent('click', (e) => {
                    e.stopPropagation();
                    folderNameSpan.addClass('hidden');
                    editButton.addClass('hidden');
                    deleteButton.addClass('hidden');
                    editInput.removeClass('hidden');
                    okButton.removeClass('hidden');
                    editInput.bindedElement.focus();
                });

                okButton.onEvent('click', (e) => {
                    e.stopPropagation();
                    const newName = editInput.getValue();
                    let userImageFolder = new UserImageFolders();
                    userImageFolder.id = folderData.id;
                    userImageFolder.get();
                    userImageFolder.display_name = newName;
                    userImageFolder.update();

                    folderNameSpan.removeClass('hidden').setContent(newName);
                    editButton.removeClass('hidden');
                    deleteButton.removeClass('hidden');
                    editInput.addClass('hidden');
                    okButton.addClass('hidden');
                });

                editInput.onEvent('click', (e) => e.stopPropagation()); // Prevent folder navigation

                let deleteBtnZIndex = this.z_index + 10;
                deleteButton.onEvent('click', (e) => {
                    e.stopPropagation();
                    let areYouSure = new ContentOverlay(
                        new Element()
                            .addClass('bg-white p-10')
                            .addContent(
                                new Element()
                                .addContent("Are you sure you want to delete this folder and all it's contents?")
                                .addClass(`my-5`))
                            .addContent(
                                new Element().addClass('flex justify-around items-center')
                                    .addContent(
                                        new Button('Delete', Color.RED)
                                            .onEvent('click', () => {
                                                Dao.request('/folder/delete', {
                                                    folder_id: folderData.id 
                                                }, (response) => {
                                                    this.refreshUserImages(this.currentFolderId);
                                                    this.updateSelectionUI();
                                                })
                                                this.removeContent(areYouSure);
                                            })
                                    )
                                    .addContent(
                                        new Button('Cancel', Color.GRAY)
                                            .onEvent('click', () => {
                                                this.removeContent(areYouSure);
                                            })
                                    )
                            ), deleteBtnZIndex
                    )
                    .addListener(ContentOverlay.onCloseEvent, () => { this.removeContent(areYouSure); });
                    this.addContent(areYouSure);
                });

                folderContainer.addContent(editButton).addContent(deleteButton).addContent(folderIcon).addContent(folderNameContainer);
                folderContainer.onEvent('click', () => {this.refreshUserImages(folderData.id); this.updateSelectionUI();});

                // Manually control button visibility on hover
                folderContainer.onEvent('mouseenter', () => {
                    // Only show buttons if not in edit mode
                    if (editInput.hasClass('hidden')) {
                        deleteButton.removeClass('hidden');
                        editButton.removeClass('hidden');
                    }
                });

                folderContainer.onEvent('mouseleave', () => {
                    deleteButton.addClass('hidden');
                    editButton.addClass('hidden');
                });

                this.photoGrid.addContent(folderContainer);
            });

            // Render Images
            response.data.images.forEach(imageData => {
                const imageContainer = new Element('div')
                    .addClass('cursor-pointer').addClass('group').addClass('relative').addClass('flex').addClass('flex-col')
                    .addClass('p-2').addClass('border').addClass('border-transparent') // Start with transparent border
                    .addClass('rounded-lg').addClass('hover:border-blue-500') // Highlight with border color
                    .addClass('hover:shadow-md').addClass('transition-all');

                // Store a reference to the container on the image data for easy access
                imageData.element = imageContainer;

                const img = new Element('img')
                    .addAttribute('src', `/image/${imageData.uuid}`)
                    .addAttribute('alt', imageData.display_name)
                    .addClass('w-full').addClass('h-32').addClass('object-cover').addClass('rounded-lg');

                // --- Name Container ---
                // Use flexbox to manage the layout of name, input, and buttons.
                const nameContainer = new Element('div')
                    .addClass('flex').addClass('items-center').addClass('justify-center')
                    .addClass('mt-2').addClass('w-full').addClass('space-x-2');

                // --- Display Name (Read-only) ---
                const imageNameSpan = new Element('span')
                    .addClass('text-sm').addClass('font-medium').addClass('text-gray-700')
                    .addClass('truncate').addContent(imageData.display_name);

                // --- Edit Button ---
                const editButton = new Element('button')
                    .addClass('p-1').addClass('bg-white').addClass('rounded-full').addClass('shadow h-5 w-5')
                    .addClass('hover:bg-gray-100').addClass('hidden') // Hidden by default
                    .addContent(new PencilIcon());

                // --- Input for Editing ---
                const editInput = new Element('input')
                    .addAttribute('type', 'text')
                    .addAttribute('value', imageData.display_name)
                    .addClass('text-sm').addClass('w-full').addClass('border-gray-300').addClass('rounded-md')
                    .addClass('shadow-sm').addClass('hidden')
                    .onEvent('click', (e) => {
                        e.stopPropagation();
                    });

                // --- OK Button (for saving) ---
                const okButton = new Element('button')
                    .addAttribute('type', 'button')
                    .addClass('px-3').addClass('py-1').addClass('text-sm').addClass('font-medium').addClass('text-white')
                    .addClass('bg-green-600').addClass('hover:bg-green-700').addClass('border').addClass('border-transparent')
                    .addClass('rounded-md').addClass('shadow-sm').addClass('hidden') // Hidden by default
                    .addContent('OK');

                // --- Delete Button for Image ---
                const deleteImageButton = new Element('button')
                    .addClass('absolute').addClass('top-1').addClass('right-1').addClass('p-1')
                    .addClass('bg-white').addClass('rounded-full').addClass('shadow h-8 w-8')
                    .addClass('hover:bg-red-100').addClass('hidden') // Hidden by default
                    .addContent(new TrashIcon());

                deleteImageButton.onEvent('click', (e) => {
                    e.stopPropagation();
                    this.deleteImages([imageData.uuid]);
                });

                // --- Event Handlers ---
                editButton.onEvent('click', (e) => {
                    e.stopPropagation(); // Prevent image deselection
                    imageNameSpan.addClass('hidden');
                    editButton.addClass('hidden');
                    editInput.removeClass('hidden');
                    okButton.removeClass('hidden');
                    editInput.bindedElement.focus();
                    editInput.bindedElement.select();
                });

                okButton.onEvent('click', (e) => {
                    e.stopPropagation(); // Prevent image deselection
                    
                    let userImage = new UserImage();
                    userImage.id = imageData.id;
                    userImage.get(); // retrieve data

                    const newName = editInput.getValue();

                    userImage.display_name = newName;
                    userImage.update();
                    
                    // Update UI
                    imageNameSpan.setContent(newName);
                    editInput.addClass('hidden');
                    okButton.addClass('hidden');
                    imageNameSpan.removeClass('hidden');
                    editButton.removeClass('hidden');
                });

                // Store references for easy access
                imageData.editButton = editButton;
                imageData.nameSpan = imageNameSpan;
                imageData.nameInput = editInput;
                imageData.okButton = okButton;

                // Add elements to the container
                nameContainer.addContent(imageNameSpan).addContent(editButton)
                             .addContent(editInput).addContent(okButton);
                
                const imageWrapper = new Element('div').addClass('relative');
                imageWrapper.addContent(img).addContent(nameContainer).addContent(deleteImageButton);
                imageContainer.addContent(imageWrapper);

                // Manually control delete button visibility on hover
                imageContainer.onEvent('mouseenter', () => {
                    deleteImageButton.removeClass('hidden');
                });

                imageContainer.onEvent('mouseleave', () => {
                    deleteImageButton.addClass('hidden');
                });

                imageContainer.onEvent('click', () => {
                    this.toggleImageSelection(imageData, response.data.images);
                });

                this.photoGrid.addContent(imageContainer);
            });
        }, 'GET');
    }

    /**
     * Toggles the selection state of an image.
     * @param {object} imageData - The data object for the clicked image.
     * @param {object[]} allImages - All images in the current view.
     */
    toggleImageSelection(imageData, allImages) {
        const { uuid, element, editButton, nameSpan, nameInput, okButton } = imageData;
        const isSelected = this.selectedImages.includes(uuid);

        if (isSelected) {
            // Deselect
            this.selectedImages = this.selectedImages.filter(id => id !== uuid);
            element.removeClass('border-2').removeClass('border-blue-600').addClass('border-transparent');
            if (editButton) editButton.addClass('hidden');
            // If the input was visible, hide it and show the span
            if (!nameInput.bindedElement.classList.contains('hidden')) {
                nameInput.addClass('hidden');
                nameSpan.removeClass('hidden');
                okButton.addClass('hidden');
                editButton.removeClass('hidden');
            }
        } else {
            // Select
            if (!this.multiSelect && this.selectedImages.length > 0) {
                // Deselect the currently selected image if multiSelect is off
                const previousSelectedId = this.selectedImages[0];
                const previousImage = allImages.find(img => img.uuid === previousSelectedId);
                if (previousImage && previousImage.element) {
                    previousImage.element.removeClass('border-2').removeClass('border-blue-600').addClass('border-transparent');
                    if (previousImage.editButton) previousImage.editButton.addClass('hidden');
                    // Also reset the name input if it was open
                    if (previousImage.nameInput && !previousImage.nameInput.bindedElement.classList.contains('hidden')) {
                        previousImage.nameInput.addClass('hidden');
                        previousImage.nameSpan.removeClass('hidden');
                        previousImage.okButton.addClass('hidden');
                        previousImage.editButton.removeClass('hidden');
                    }
                }
                this.selectedImages = [];
            }
            this.selectedImages.push(uuid);
            element.addClass('border-2').addClass('border-blue-600').removeClass('border-transparent');
            if (editButton) editButton.removeClass('hidden');
        }

        this.updateSelectionUI();
    }

    /**
     * Updates the UI elements related to selection, like the 'Use' button text.
     */
    updateSelectionUI() {
        if (this.canSelect && this.useButton) {
            const hasSelection = this.selectedImages.length > 0;

            if (hasSelection) {
                this.useButton.removeProperty('disabled');
                this.useButton.removeClass('bg-gray-400').removeClass('cursor-not-allowed')
                             .addClass('bg-blue-600').addClass('hover:bg-blue-700');
            } else {
                this.useButton.addAttribute('disabled', true);
                this.useButton.removeClass('bg-blue-600').removeClass('hover:bg-blue-700')
                             .addClass('bg-gray-400').addClass('cursor-not-allowed');
            }

            if (this.multiSelect && hasSelection) {
                this.useButton.setContent(`Use (${this.selectedImages.length} selected)`);
            } else {
                this.useButton.setContent('Use');
            }
        }
        // You can also update the selection count text if needed
        const countSpan = this.bindedElement.querySelector('#selected-count');
        if (countSpan) {
            countSpan.textContent = this.selectedImages.length;
        }
        
        // Update delete button visibility
        if (this.deleteSelectionButton) {
            if (this.selectedImages.length > 0) {
                this.deleteSelectionButton.removeClass('hidden');
                this.deleteSelectionButton.setContent('Delete ('+this.selectedImages.length+')');
            } else {
                this.deleteSelectionButton.addClass('hidden');
            }
        }
    }

    /**
     * Deletes images based on their UUIDs.
     * @param {string[]} uuids - Array of image UUIDs to delete.
     */
    deleteImages(uuids = []) {
        let totalDeleted = 0;
        let deleteBtnZIndex = this.z_index + 10;
        let areYouSure = new ContentOverlay(
            new Element()
                .addClass(`bg-white p-10`)
                .addContent(
                    new Element()
                        .addContent("Are you sure you want to delete all selected images?")
                        .addClass('my-5'))
                .addContent(
                    new Element().addClass('flex justify-around items-center')
                        .addContent(
                            new Button('Delete', Color.RED)
                                .onEvent('click', () => {
                                    uuids.forEach(uuid => {
                                        Dao.request('/image/delete', {
                                            uuid: uuid
                                        }, (response) => {
                                            if(response.success){
                                                totalDeleted++;
                                            }
                                            if(totalDeleted == uuids.length){
                                                this.selectedImages = [];
                                                this.refreshUserImages(this.currentFolderId);
                                                this.updateSelectionUI();
                                            }
                                        })
                                    })
                                    this.removeContent(areYouSure);
                                })
                        )
                        .addContent(
                            new Button('Cancel', Color.GRAY)
                                .onEvent('click', () => {
                                    this.removeContent(areYouSure);
                                })
                        )
                ), deleteBtnZIndex
        )
        .addListener(ContentOverlay.onCloseEvent, () => { this.removeContent(areYouSure); })
        this.addContent(areYouSure);
    }

    handleFiles(files) {
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/tiff', 'image/svg+xml'];
        const fileList = Array.from(files).filter(file => allowedTypes.includes(file.type));

        if (fileList.length === 0) {
            // Optionally, show an error message if no valid files are selected/dropped
            console.warn("No valid image files selected for upload.");
            return;
        }

        this.uploadProgressBar.show();
        this.uploadProgressBar.update(0, fileList.length);

        let completedUploads = 0;

        const uploadPromises = fileList.map(file => 
            this.uploadFile(file).then(response => {
                completedUploads++;
                this.uploadProgressBar.update(completedUploads, fileList.length);
                return response;
            })
        );

        Promise.all(uploadPromises).then(() => {
            console.log("All uploads completed.");
            setTimeout(() => {
                this.uploadProgressBar.hide();
                this.refreshUserImages(this.currentFolderId);
                this.updateSelectionUI();
            }, 1000); // Wait a second before hiding and refreshing
        }).catch(error => {
            console.error("An error occurred during one or more uploads:", error);
            setTimeout(() => {
                this.uploadProgressBar.hide();
                this.refreshUserImages(this.currentFolderId);
                this.updateSelectionUI();
            }, 1000);
        });
    }

    uploadFile(file) {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append('uploaded_file', file);
            if (this.currentFolderId) {
                formData.append('folder_id', this.currentFolderId);
            }
            Dao.request('/image/upload', formData, 
                (response) => {
                    if (response.success) {
                        console.log(`Successfully uploaded ${file.name}`);
                        resolve(response);
                    } else {
                        console.error(`Failed to upload ${file.name}: ${response.message}`);
                        reject(response);
                    }
                },
                'POST'
            );
        });
    }

    open() {
        this.modalBackdrop.removeClass('hidden');
    }

    close() {
        this.modalBackdrop.addClass('hidden');
        this.event(PhotoManager.onCloseEvent, this);
    }

}
